import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { atom, AtomEffect } from "recoil";
import { persistAtom } from "./persist";
import { IAmsCard, ICustomSeverity } from "MVVM/Components/QuickEdit/@types";

// Runs after persistAtom during atom initialization and replaces a
// stale/corrupt rehydrated value with a safe default. An older localStorage
// shape (e.g. a saved song missing `ui.title`) would otherwise crash the
// Fragmovie editor render on every open. Only the rehydrated value is
// validated — later in-app updates are left untouched.
const sanitizeOnLoad =
  <T>(isValid: (value: unknown) => boolean, fallback: T): AtomEffect<T> =>
  ({ setSelf }) => {
    setSelf((current) =>
      isValid(current as unknown) ? (current as T) : fallback,
    );
  };

const isValidSong = (value: unknown): boolean =>
  !!value &&
  typeof value === "object" &&
  typeof (value as IAmsCard)?.ui?.title === "string";

const SEVERITY_FIELDS: (keyof ICustomSeverity)[] = [
  "lead",
  "hit",
  "post",
  "lead_duration",
  "hit_duration",
  "post_duration",
];

const isValidSeverity = (value: unknown): boolean =>
  !!value &&
  typeof value === "object" &&
  SEVERITY_FIELDS.every(
    (field) => typeof (value as Record<string, unknown>)[field] === "number",
  );

export const QuickEditTitle = atom<string>({
  key: "QuickEditTitle",
  default: "",
});

export const QuickEditPreferredLoadout = atom<TLoadout | null>({
  key: "QuickEditPreferredLoadoutV2",
  default: null,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditPreferredFinalTouches = atom<string[]>({
  key: "QuickEditPreferredFinalTouches",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditPreferredThumbnail = atom<string>({
  key: "QuickEditPreferredThumbnailV2",
  default: "",
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditPreferredPresetId = atom<string | null>({
  key: "QuickEditPreferredPresetId",
  default: null,
  effects_UNSTABLE: [persistAtom],
});

// Time Remap (Fragmovie) persisted settings

export const QuickEditTimeRemapSelectedSong = atom<IAmsCard | null>({
  key: "QuickEditTimeRemapSelectedSong",
  default: null,
  effects_UNSTABLE: [
    persistAtom,
    sanitizeOnLoad<IAmsCard | null>(
      (value) => value === null || isValidSong(value),
      null,
    ),
  ],
});

export const QuickEditTimeRemapSongVolume = atom<number>({
  key: "QuickEditTimeRemapSongVolume",
  default: 40,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapCustomVolumeEnabled = atom<boolean>({
  key: "QuickEditTimeRemapCustomVolumeEnabled",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapImpactSeverity = atom<number>({
  key: "QuickEditTimeRemapImpactSeverity",
  default: 30,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapCustomSeverityEnabled = atom<boolean>({
  key: "QuickEditTimeRemapCustomSeverityEnabled",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

const defaultCustomSeverity: ICustomSeverity = {
  lead: 0.5,
  hit: 0.55,
  post: 0.2,
  lead_duration: 0.1,
  hit_duration: 0.1,
  post_duration: 0.7,
};

export const QuickEditTimeRemapCustomSeverity = atom<ICustomSeverity>({
  key: "QuickEditTimeRemapCustomSeverity",
  default: defaultCustomSeverity,
  effects_UNSTABLE: [
    persistAtom,
    sanitizeOnLoad<ICustomSeverity>(isValidSeverity, defaultCustomSeverity),
  ],
});

export const QuickEditTimeRemapLimitSlowmo = atom<boolean>({
  key: "QuickEditTimeRemapLimitSlowmo",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapUseHudSettings = atom<boolean>({
  key: "QuickEditTimeRemapUseHudSettings",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapEventBreathingRoom = atom<boolean>({
  key: "QuickEditTimeRemapEventBreathingRoom",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapSpeedupSyncEnabled = atom<boolean>({
  key: "QuickEditTimeRemapSpeedupSyncEnabled",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapSpeedupMaxVelocity = atom<number>({
  key: "QuickEditTimeRemapSpeedupMaxVelocity",
  default: 30,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapSpeedupBeatFrequency = atom<number>({
  key: "QuickEditTimeRemapSpeedupBeatFrequency",
  default: 0.25,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapSpeedupVfxEnabled = atom<boolean>({
  key: "QuickEditTimeRemapSpeedupVfxEnabled",
  default: true,
  effects_UNSTABLE: [persistAtom],
});

export const QuickEditTimeRemapApplyStudioCards = atom<boolean>({
  key: "QuickEditTimeRemapApplyStudioCards",
  default: false,
  effects_UNSTABLE: [persistAtom],
});
